import { createContext, useContext } from "react";
import type { Navigator, To } from "react-router-dom";

export type NavigationGuard = (to: To, proceed: () => void) => void;
export type RegisterNavigationGuard = (guard: NavigationGuard) => () => void;

export const NavigationGuardContext = createContext<RegisterNavigationGuard | null>(null);

/** An imperative adapter with its own registration lifetime and unchanged source history. */
export function createGuardedNavigator(navigator: Navigator) {
  let current: NavigationGuard | null = null;
  const register: RegisterNavigationGuard = (guard) => {
    current = guard;
    return () => {
      if (current === guard) current = null;
    };
  };
  const wrap =
    (method: "push" | "replace") =>
    (...args: Parameters<Navigator["push"]>) => {
      const proceed = () => {
        if (method === "push") navigator.push(...args);
        else navigator.replace(...args);
      };
      if (current) current(args[0], proceed);
      else proceed();
    };
  return {
    register,
    navigator: { ...navigator, push: wrap("push"), replace: wrap("replace") },
  };
}

export function useRegisterNavigationGuard(): RegisterNavigationGuard {
  const register = useContext(NavigationGuardContext);
  if (!register) throw new Error("Navigation guards require NavigationGuardProvider");
  return register;
}
